import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Building2,
    Check,
    Copy,
    CreditCard,
    Eye,
    EyeOff,
    FileText,
    MapPin,
    User,
} from "lucide-react";
import { COMPANY_ADD } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Input, Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { FormLabel, AuthSubmitButton, AuthTextLink } from "../../components/ui/form-fields";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { brandIconWrap } from "../../components/ui/statusStyles";
import { cn } from "../../utils/tailwindUtils";
import appLogo from "../../assets/png/HRZyra-Logo.png";

const INITIAL_FORM = {
    company_name: "",
    brand_name: "",
    official_email_address: "",
    landline: "",
    domain_name: "",
    registered_address: "",
    corporate_address: "",
    other_address_1: "",
    other_address_2: "",
    contact_person: "",
    contact_person_number: "",
    contact_person_email_address: "",
    pan_no: "",
    gstn_number: "",
    certificate_of_incorporation: "",
    prefix: "",
    city: "",
    block: "",
    room: "",
    floor: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GST_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const DOMAIN_RE = /^[A-Za-z0-9][A-Za-z0-9.\-]*\.[A-Za-z]{2,}$/;
const PHONE_RE = /^\d{10}$/;
const LANDLINE_RE = /^\d{6,15}$/;

const FIELD_LIMITS = {
    company_name: 100,
    brand_name: 32,
    official_email_address: 100,
    landline: 15,
    domain_name: 50,
    registered_address: 100,
    corporate_address: 100,
    other_address_1: 100,
    other_address_2: 100,
    contact_person: 32,
    contact_person_number: 14,
    contact_person_email_address: 100,
    pan_no: 10,
    gstn_number: 15,
    certificate_of_incorporation: 32,
    prefix: 6,
    city: 100,
    block: 32,
    room: 32,
    floor: 32,
};

const normalizeText = (value) => (value ?? "").toString().replace(/\s+/g, " ").trim();
const hasLetters = (value) => /[A-Za-z]/.test(value);

const formatPrice = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const amount = Number(value);
    if (Number.isNaN(amount)) return String(value);
    return `₹${amount.toLocaleString("en-IN")}`;
};

const SectionCard = ({ icon: Icon, title, children }) => (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-5">
        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-border">
            <span className={cn("w-9 h-9 flex-shrink-0 rounded-xl grid place-items-center", brandIconWrap)}>
                <Icon className="h-4 w-4" />
            </span>
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
        </div>
        {children}
    </div>
);

const Field = ({
    label,
    name,
    value,
    onChange,
    type = "text",
    placeholder,
    required,
    hint,
    maxLength,
    className,
}) => (
    <div className={cn("space-y-1.5", className)}>
        <FormLabel htmlFor={name} required={required} className="text-xs uppercase tracking-wide text-muted-foreground mb-0">
            {label}
        </FormLabel>
        <Input
            id={name}
            type={type}
            name={name}
            value={value ?? ""}
            onChange={onChange}
            placeholder={placeholder}
            maxLength={maxLength}
        />
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
);

const CopyField = ({ label, value, secret = false }) => {
    const [visible, setVisible] = useState(!secret);
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            ToastMsg("error", "Unable to copy");
        }
    };

    return (
        <div className="rounded-xl border border-border bg-muted/50 p-3">
            <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 font-mono text-sm text-foreground truncate">
                    {visible ? (value || "—") : "••••••••"}
                </div>
                {secret && (
                    <button
                        type="button"
                        onClick={() => setVisible((prev) => !prev)}
                        className="text-muted-foreground hover:text-sidebar-primary transition-colors"
                        aria-label={visible ? "Hide value" : "Show value"}
                    >
                        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                )}
                <button
                    type="button"
                    onClick={copy}
                    className="text-muted-foreground hover:text-sidebar-primary transition-colors"
                    aria-label={`Copy ${label}`}
                >
                    {copied ? <Check className="h-4 w-4 text-sidebar-primary" /> : <Copy className="h-4 w-4" />}
                </button>
            </div>
        </div>
    );
};

const CreateOrganization = () => {
    const navigate = useNavigate();
    const { fetch, create } = useApiMethods();

    const [form, setForm] = useState(INITIAL_FORM);
    const [plans, setPlans] = useState([]);
    const [planId, setPlanId] = useState(null);
    const [billingCycle, setBillingCycle] = useState("yearly");
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [credentials, setCredentials] = useState(null);

    useEffect(() => {
        const loadPlans = async () => {
            try {
                const response = await fetch(COMPANY_ADD, {}, setLoadingPlans);
                if (response?.status === "success") {
                    const list = Array.isArray(response.data) ? response.data : response.data?.plans || [];
                    setPlans(list);
                    if (list.length === 1) setPlanId(list[0].id);
                } else {
                    ToastMsg("error", response?.message || "Failed to load plans");
                }
            } catch (error) {
                ToastMsg("error", error.message || "Failed to load plans");
            }
        };
        loadPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!submitting) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [submitting]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let nextValue = value;
        if (name === "prefix") {
            nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, FIELD_LIMITS.prefix);
        } else if (name === "pan_no") {
            nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, FIELD_LIMITS.pan_no);
        } else if (name === "gstn_number") {
            nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, FIELD_LIMITS.gstn_number);
        } else if (name === "landline" || name === "contact_person_number") {
            nextValue = value.replace(/\D/g, "").slice(0, FIELD_LIMITS[name]);
        } else if (FIELD_LIMITS[name]) {
            nextValue = value.slice(0, FIELD_LIMITS[name]);
        }
        setForm((prev) => ({
            ...prev,
            [name]: nextValue,
        }));
    };

    const validate = () => {
        const companyName = normalizeText(form.company_name);
        if (!companyName) {
            ToastMsg("error", "Company name is required");
            return false;
        }
        if (!hasLetters(companyName)) {
            ToastMsg("error", "Company name must contain at least one letter");
            return false;
        }
        const prefix = normalizeText(form.prefix).toUpperCase();
        if (!prefix) {
            ToastMsg("error", "Organization prefix is required");
            return false;
        }
        if (prefix.length < 2) {
            ToastMsg("error", "Prefix must be at least 2 characters");
            return false;
        }
        const brandName = normalizeText(form.brand_name);
        if (brandName && !hasLetters(brandName)) {
            ToastMsg("error", "Brand name must contain at least one letter");
            return false;
        }
        const domainName = normalizeText(form.domain_name);
        if (domainName && !DOMAIN_RE.test(domainName)) {
            ToastMsg("error", "Please enter a valid domain name");
            return false;
        }
        const officialEmail = normalizeText(form.official_email_address);
        if (!officialEmail) {
            ToastMsg("error", "Official email is required");
            return false;
        }
        if (!EMAIL_RE.test(officialEmail)) {
            ToastMsg("error", "Please enter a valid official email address");
            return false;
        }
        const contactEmail = normalizeText(form.contact_person_email_address);
        if (contactEmail && !EMAIL_RE.test(contactEmail)) {
            ToastMsg("error", "Please enter a valid contact person email");
            return false;
        }
        const pan = normalizeText(form.pan_no).toUpperCase();
        if (pan && !PAN_RE.test(pan)) {
            ToastMsg("error", "Please enter a valid PAN number");
            return false;
        }
        const gst = normalizeText(form.gstn_number).toUpperCase();
        if (gst) {
            if (gst.length > FIELD_LIMITS.gstn_number) {
                ToastMsg("error", `GST number must be at most ${FIELD_LIMITS.gstn_number} characters`);
                return false;
            }
            if (!GST_RE.test(gst)) {
                ToastMsg("error", "Please enter a valid GST number");
                return false;
            }
        }
        const landline = normalizeText(form.landline);
        if (landline && !LANDLINE_RE.test(landline)) {
            ToastMsg("error", "Landline must contain 6 to 15 digits");
            return false;
        }
        const contactNumber = normalizeText(form.contact_person_number);
        if (contactNumber && !PHONE_RE.test(contactNumber)) {
            ToastMsg("error", "Contact number must be 10 digits");
            return false;
        }
        const contactPerson = normalizeText(form.contact_person);
        if (contactPerson && !hasLetters(contactPerson)) {
            ToastMsg("error", "Contact person name must contain at least one letter");
            return false;
        }
        if (!planId) {
            ToastMsg("error", "Please choose a plan");
            return false;
        }
        return true;
    };

    const buildPayload = () => {
        const payload = { ...form };
        Object.keys(payload).forEach((key) => {
            if (typeof payload[key] === "string") {
                payload[key] = key === "prefix" || key === "pan_no" || key === "gstn_number"
                    ? normalizeText(payload[key]).toUpperCase()
                    : normalizeText(payload[key]);
            }
        });
        payload.prefix = payload.prefix.toUpperCase();
        payload.plan_id = planId;
        payload.billing_cycle = billingCycle;
        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = buildPayload();

        try {
            const response = await create(COMPANY_ADD, payload, false, setSubmitting, { timeout: 180000 });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Organization created successfully");
                setCredentials({
                    company_name: response.company_name || form.company_name,
                    organization: response.organization || form.prefix,
                    employee_code: response.employee_code || "",
                    password: response.password || "",
                    user_name: response.user_name || "",
                    message: response.message || "",
                });
            } else {
                ToastMsg("error", response?.message || "Failed to create organization");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Failed to create organization");
        }
    };

    if (credentials) {
        return (
            <div className="min-h-screen bg-background font-sans flex items-center justify-center p-6">
                <div className="w-full max-w-lg bg-card rounded-2xl shadow-xl border border-border p-6">
                    <div className="flex justify-center mb-4">
                        <img src={appLogo} alt="App Logo" className="h-10 w-auto" />
                    </div>
                    <div className="text-center mb-5">
                        <div className={cn("mx-auto mb-3 w-12 h-12 rounded-full grid place-items-center", brandIconWrap)}>
                            <Check className="h-6 w-6" />
                        </div>
                        <h1 className="text-xl font-semibold text-foreground">Organization created</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Save these login details. Use organization, employee code and password to sign in.
                        </p>
                    </div>
                    <div className="space-y-3">
                        <CopyField label="Organization name" value={credentials.company_name} />
                        <CopyField label="Organization (login)" value={credentials.organization} />
                        <CopyField label="Employee code" value={credentials.employee_code} />
                        <CopyField label="Password" value={credentials.password} secret />
                    </div>
                    <AuthSubmitButton
                        type="button"
                        onClick={() => navigate("/")}
                        className="mt-6"
                    >
                        Go to Sign In
                    </AuthSubmitButton>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background font-sans">
            <div className="max-w-6xl mx-auto px-4 py-8">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                    <div className="flex items-start gap-3">
                        <img src={appLogo} alt="App Logo" className="h-10 w-auto" />
                        <div>
                            <h1 className="text-xl font-semibold text-foreground">Create organization</h1>
                            <p className="text-sm text-muted-foreground mt-1">
                                Register a new company, choose a plan, and receive admin login credentials.
                            </p>
                        </div>
                    </div>
                    <AuthTextLink
                        to="/"
                        onClick={(e) => submitting && e.preventDefault()}
                        className={cn(submitting && "pointer-events-none opacity-50 cursor-not-allowed")}
                    >
                        Back to Sign In
                    </AuthTextLink>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <SectionCard icon={Building2} title="Company details">
                            <div className="space-y-4">
                                <Field
                                    label="Company Name"
                                    name="company_name"
                                    value={form.company_name}
                                    onChange={handleChange}
                                    placeholder="Enter company name"
                                    required
                                    maxLength={FIELD_LIMITS.company_name}
                                />
                                <Field
                                    label="Brand Name"
                                    name="brand_name"
                                    value={form.brand_name}
                                    onChange={handleChange}
                                    placeholder="Enter brand name"
                                    maxLength={FIELD_LIMITS.brand_name}
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field
                                        label="Prefix"
                                        name="prefix"
                                        value={form.prefix}
                                        onChange={handleChange}
                                        placeholder="Enter prefix"
                                        required
                                        maxLength={6}
                                        hint="Used as organization code at login. Cannot be changed later."
                                    />
                                    <Field
                                        label="Domain Name"
                                        name="domain_name"
                                        value={form.domain_name}
                                        onChange={handleChange}
                                        placeholder="Enter domain name"
                                        maxLength={FIELD_LIMITS.domain_name}
                                    />
                                </div>
                                <Field
                                    label="Official Email"
                                    name="official_email_address"
                                    type="email"
                                    value={form.official_email_address}
                                    onChange={handleChange}
                                    placeholder="Enter official email"
                                    required
                                    maxLength={FIELD_LIMITS.official_email_address}
                                />
                                <Field
                                    label="Landline"
                                    name="landline"
                                    value={form.landline}
                                    onChange={handleChange}
                                    placeholder="Enter landline number"
                                    maxLength={FIELD_LIMITS.landline}
                                />
                            </div>
                        </SectionCard>

                        <SectionCard icon={MapPin} title="Address">
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <FormLabel htmlFor="registered_address" className="text-xs uppercase tracking-wide text-muted-foreground mb-0">
                                        Registered Address
                                    </FormLabel>
                                    <Textarea
                                        id="registered_address"
                                        name="registered_address"
                                        value={form.registered_address}
                                        onChange={handleChange}
                                        placeholder="Enter registered address"
                                        rows={3}
                                        maxLength={FIELD_LIMITS.registered_address}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <FormLabel htmlFor="corporate_address" className="text-xs uppercase tracking-wide text-muted-foreground mb-0">
                                        Corporate Address
                                    </FormLabel>
                                    <Textarea
                                        id="corporate_address"
                                        name="corporate_address"
                                        value={form.corporate_address}
                                        onChange={handleChange}
                                        placeholder="Enter corporate address"
                                        rows={3}
                                    />
                                </div>
                                <Field
                                    label="Other Address 1"
                                    name="other_address_1"
                                    value={form.other_address_1}
                                    onChange={handleChange}
                                    placeholder="Enter other address 1"
                                />
                                <Field
                                    label="Other Address 2"
                                    name="other_address_2"
                                    value={form.other_address_2}
                                    onChange={handleChange}
                                    placeholder="Enter other address 2"
                                />
                                <div className="grid grid-cols-2 gap-4">
                                    <Field label="City" name="city" value={form.city} onChange={handleChange} placeholder="Enter city" />
                                    <Field label="Block" name="block" value={form.block} onChange={handleChange} placeholder="Enter block" />
                                    <Field label="Room" name="room" value={form.room} onChange={handleChange} placeholder="Enter room" />
                                    <Field label="Floor" name="floor" value={form.floor} onChange={handleChange} placeholder="Enter floor" />
                                </div>
                            </div>
                        </SectionCard>

                        <SectionCard icon={User} title="Contact person">
                            <div className="space-y-4">
                                <Field
                                    label="Contact Person"
                                    name="contact_person"
                                    value={form.contact_person}
                                    onChange={handleChange}
                                    placeholder="Enter contact person name"
                                />
                                <Field
                                    label="Contact Number"
                                    name="contact_person_number"
                                    value={form.contact_person_number}
                                    onChange={handleChange}
                                    placeholder="Enter contact number"
                                    maxLength={FIELD_LIMITS.contact_person_number}
                                />
                                <Field
                                    label="Contact Email"
                                    name="contact_person_email_address"
                                    type="email"
                                    value={form.contact_person_email_address}
                                    onChange={handleChange}
                                    placeholder="Enter contact email"
                                    maxLength={FIELD_LIMITS.contact_person_email_address}
                                />
                            </div>
                        </SectionCard>

                        <SectionCard icon={FileText} title="Statutory">
                            <div className="space-y-4">
                                <Field
                                    label="PAN"
                                    name="pan_no"
                                    value={form.pan_no}
                                    onChange={handleChange}
                                    placeholder="Enter PAN number"
                                    maxLength={10}
                                />
                                <Field
                                    label="GSTN Number"
                                    name="gstn_number"
                                    value={form.gstn_number}
                                    onChange={handleChange}
                                    placeholder="Enter GSTN number"
                                    maxLength={FIELD_LIMITS.gstn_number}
                                    hint="Maximum 15 characters"
                                />
                                <Field
                                    label="Certificate of Incorporation"
                                    name="certificate_of_incorporation"
                                    value={form.certificate_of_incorporation}
                                    onChange={handleChange}
                                    placeholder="Enter certificate of incorporation"
                                />
                            </div>
                        </SectionCard>
                    </div>

                    <SectionCard icon={CreditCard} title="Choose a plan">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                            <p className="text-sm text-muted-foreground">
                                Plans are loaded from the main HRMS database.
                            </p>
                            <SegmentSwitch
                                options={[
                                    { value: "monthly", label: "Monthly" },
                                    { value: "yearly", label: "Yearly" },
                                ]}
                                value={billingCycle}
                                onChange={setBillingCycle}
                            />
                        </div>

                        {loadingPlans ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {[1, 2, 3].map((item) => (
                                    <div key={item} className="h-36 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse" />
                                ))}
                            </div>
                        ) : plans.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                No active plans found. Add a plan in the main database before creating an organization.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {plans.map((plan) => {
                                    const selected = planId === plan.id;
                                    const price =
                                        billingCycle === "yearly" ? plan.price_per_year : plan.price_per_month;
                                    const modules = Array.isArray(plan.modules) ? plan.modules : [];
                                    return (
                                        <button
                                            key={plan.id}
                                            type="button"
                                            onClick={() => setPlanId(plan.id)}
                                            className={cn(
                                                "text-left rounded-xl border p-4 transition shadow-sm",
                                                selected
                                                    ? "border-sidebar-primary ring-2 ring-sidebar-primary/30 bg-sidebar/10"
                                                    : "border-border hover:border-sidebar-primary/50 bg-card"
                                            )}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <div className="font-semibold text-foreground">
                                                        {plan.packege_name || "Untitled plan"}
                                                    </div>
                                                    <div className="text-sm text-sidebar-primary font-medium mt-1">
                                                        {formatPrice(price)}
                                                        <span className="text-muted-foreground font-normal">
                                                            /{billingCycle === "yearly" ? "year" : "month"}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span
                                                    className={cn(
                                                        "w-5 h-5 rounded-full border grid place-items-center flex-shrink-0",
                                                        selected
                                                            ? "bg-sidebar-primary border-sidebar-primary text-white"
                                                            : "border-border"
                                                    )}
                                                >
                                                    {selected && <Check className="h-3 w-3" />}
                                                </span>
                                            </div>
                                            {plan.packege_description && (
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-3">
                                                    {plan.packege_description}
                                                </p>
                                            )}
                                            {modules.length > 0 && (
                                                <p className="text-[11px] text-gray-400 mt-3">
                                                    {modules.length} module{modules.length === 1 ? "" : "s"} included
                                                </p>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </SectionCard>

                    <div className="flex items-center justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={submitting}
                            onClick={() => {
                                if (!submitting) navigate("/");
                            }}
                        >
                            Cancel
                        </Button>
                        <AuthSubmitButton
                            loading={submitting}
                            loadingText="Creating..."
                            disabled={loadingPlans}
                            className="min-w-40 w-auto"
                        >
                            Create organization
                        </AuthSubmitButton>
                    </div>
                    {submitting && (
                        <p className="text-xs text-muted-foreground text-right">
                            Provisioning the organization can take a minute. Keep this page open.
                        </p>
                    )}
                </form>
            </div>
        </div>
    );
};

export default CreateOrganization;
